import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Home, Droplets, MapPin, User, Building2, ShieldCheck, LogIn, LogOut, Settings } from 'lucide-react';
import NotificationBell from './NotificationBell';
import LogoImage from '../../assets/HopeForSomeone.png';

const S = {
  topNav: {
    position: 'sticky', top: 0, zIndex: 100,
    background: 'rgba(255,255,255,0.95)',
    backdropFilter: 'blur(8px)',
    borderBottom: '1px solid var(--gray-200)',
    boxShadow: 'var(--shadow-xs)',
    transition: 'box-shadow 0.25s ease',
  },
  inner: {
    maxWidth: '1200px', margin: '0 auto', padding: '0 24px',
    height: '85px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  logo: { display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' },
  // The lockup is pre-trimmed to its artwork, so these are the real rendered
  // heights — they must stay under the 85px / 60px bar heights below.
  logoImg: { height: '130px', width: 'auto', display: 'block' },
  logoImgMobile: { height: '90px', width: 'auto', display: 'block' },
  desktopLinks: { display: 'flex', alignItems: 'center', gap: '2px' },
  userChip: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '5px 10px 5px 6px',
    background: 'var(--gray-100)', borderRadius: '20px',
    fontSize: '13px', marginLeft: '8px', fontWeight: 500, color: 'var(--gray-700)',
  },
  avatar: {
    width: '26px', height: '26px', borderRadius: '50%', background: 'var(--red)',
    color: 'white', fontSize: '11px', fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  mobileTop: {
    position: 'sticky', top: 0, zIndex: 100,
    background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)',
    borderBottom: '1px solid var(--gray-200)',
    boxShadow: 'var(--shadow-xs)',
    transition: 'box-shadow 0.25s ease',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 16px', height: '60px',
  },
  bottomNav: {
    position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 200,
    background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(8px)',
    borderTop: '1px solid var(--gray-200)',
    boxShadow: '0 -2px 16px rgba(0,0,0,0.06)',
    display: 'flex', height: 'var(--bottom-nav-h)',
    paddingBottom: 'env(safe-area-inset-bottom)',
  },
  tabItem: {
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', gap: '3px', border: 'none', background: 'none',
    padding: '8px 4px', cursor: 'pointer',
  },
  tabLabel: { fontSize: '10px', fontWeight: 600, letterSpacing: '0.01em' },
};

const NAV_TABS = [
  { path: '/',              Icon: Home,      label: 'Home' },
  { path: '/requests',      Icon: Droplets,  label: 'Requests' },
  { path: '/camps',         Icon: MapPin,    label: 'Camps' },
  { path: '/donor/profile', Icon: User,      label: 'Profile', role: 'donor' },
  { path: '/org/dashboard', Icon: Building2, label: 'Org',     role: 'organization' },
  { path: '/admin', Icon: ShieldCheck, label: 'Admin', role: 'admin' },
];

function NavLink({ to, children, active }) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <Link
      to={to}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '6px 12px', borderRadius: '7px', fontSize: '13.5px',
        fontWeight: active ? 600 : 500,
        color: active ? 'var(--red)' : hovered ? 'var(--gray-800)' : 'var(--gray-500)',
        background: active ? 'var(--red-light)' : hovered ? 'var(--gray-100)' : 'transparent',
        textDecoration: 'none',
        transition: 'color 0.15s, background 0.15s',
        display: 'inline-block',
      }}
    >
      {children}
    </Link>
  );
}

function CtaLink({ to, children }) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <Link
      to={to}
      style={{
        background: hovered ? 'var(--red-dark)' : 'var(--red)',
        color: 'white', padding: '7px 16px',
        borderRadius: '8px', fontSize: '13px', fontWeight: 600,
        marginLeft: '6px', textDecoration: 'none',
        boxShadow: hovered ? 'var(--shadow-red)' : 'none',
        transform: hovered ? 'translateY(-1px)' : 'none',
        transition: 'background 0.15s, box-shadow 0.15s, transform 0.15s',
        display: 'inline-block',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </Link>
  );
}

function LogoutBtn({ onClick }) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: '5px',
        background: hovered ? 'var(--gray-100)' : 'none',
        border: '1px solid var(--gray-200)', borderRadius: '7px',
        padding: '5px 10px', fontSize: '12.5px', color: 'var(--gray-600)',
        cursor: 'pointer', marginLeft: '6px',
        transition: 'background 0.15s',
      }}
    >
      <LogOut size={13} />
      Sign out
    </button>
  );
}

function useIsMobile() {
  const [mobile, setMobile] = React.useState(window.innerWidth <= 768);
  React.useEffect(() => {
    const fn = () => setMobile(window.innerWidth <= 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return mobile;
}

function useScrolled(threshold = 8) {
  const [scrolled, setScrolled] = React.useState(window.scrollY > threshold);
  React.useEffect(() => {
    const fn = () => setScrolled(window.scrollY > threshold);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, [threshold]);
  return scrolled;
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const scrolled = useScrolled();

  const handleLogout = () => { logout(); navigate('/'); };
  const visibleTabs = NAV_TABS.filter(t => !t.role || t.role === user?.role);

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  if (isMobile) {
    return (
      <>
        <header style={{ ...S.mobileTop, boxShadow: scrolled ? 'var(--shadow-md)' : 'var(--shadow-xs)' }}>
          <Link to="/" style={S.logo}>
            <img src={LogoImage} alt="HopeForSomeone" style={S.logoImgMobile} />
          </Link>
          {!user ? (
            <Link to="/register" style={{
              background: 'var(--red)', color: 'white', padding: '6px 14px',
              borderRadius: '7px', fontSize: '12.5px', fontWeight: 600,
            }}>
              Join
            </Link>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <NotificationBell />
              <button
                onClick={() => navigate('/account/settings')}
                aria-label="Account settings"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'none', border: '1px solid var(--gray-200)', borderRadius: '7px',
                  padding: '6px', color: 'var(--gray-600)', cursor: 'pointer',
                }}
              >
                <Settings size={14} />
              </button>
              <button
                onClick={handleLogout}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  background: 'none', border: '1px solid var(--gray-200)', borderRadius: '7px',
                  padding: '5px 10px', fontSize: '12px', color: 'var(--gray-600)', cursor: 'pointer',
                }}
              >
                <LogOut size={12} />
                Sign out
              </button>
            </div>
          )}
        </header>

        <nav style={S.bottomNav}>
          {visibleTabs.map(tab => {
            const active = isActive(tab.path);
            return (
              <button
                key={tab.path}
                style={S.tabItem}
                onClick={() => navigate(tab.path)}
                aria-label={tab.label}
                aria-current={active ? 'page' : undefined}
              >
                <tab.Icon
                  size={20}
                  strokeWidth={active ? 2.5 : 1.75}
                  color={active ? 'var(--red)' : 'var(--gray-400)'}
                />
                <span style={{ ...S.tabLabel, color: active ? 'var(--red)' : 'var(--gray-400)' }}>
                  {tab.label}
                </span>
              </button>
            );
          })}
          {!user && (
            <button style={S.tabItem} onClick={() => navigate('/login')}>
              <LogIn size={20} strokeWidth={1.75} color="var(--gray-400)" />
              <span style={{ ...S.tabLabel, color: 'var(--gray-400)' }}>Sign in</span>
            </button>
          )}
        </nav>
      </>
    );
  }

  return (
    <header style={{ ...S.topNav, boxShadow: scrolled ? 'var(--shadow-md)' : 'var(--shadow-xs)' }}>
      <div style={S.inner}>
        <Link to="/" style={S.logo}>
          <img src={LogoImage} alt="HopeForSomeone" style={S.logoImg} />
        </Link>

        <div style={S.desktopLinks}>
          <NavLink to="/requests"     active={isActive('/requests')}>Requests</NavLink>
          <NavLink to="/camps"        active={isActive('/camps')}>Camps</NavLink>
          {/* Signed-out visitors are the ones who need the explainer, and their
              nav has the most room — a signed-in donor already carries profile,
              certificates, the bell and the account chip. Footer link covers
              everyone else. */}
          {!user && <NavLink to="/how-it-works" active={isActive('/how-it-works')}>How it works</NavLink>}
          <NavLink to="/donors/search" active={isActive('/donors/search')}>Find Donors</NavLink>
          {user?.role === 'organization' && <NavLink to="/org/dashboard" active={isActive('/org/dashboard')}>Dashboard</NavLink>}
          {user?.role === 'donor'        && <NavLink to="/donor/profile" active={isActive('/donor/profile')}>My Profile</NavLink>}
          {user?.role === 'donor'        && <NavLink to="/donor/certificates" active={isActive('/donor/certificates')}>Certificates</NavLink>}
          {user?.role === 'admin'        && <NavLink to="/admin" active={isActive('/admin')}>Admin</NavLink>}

          {user ? (
            <>
              <NotificationBell />
              <Link
                to="/account/settings"
                aria-label="Account settings"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '28px', height: '28px', marginLeft: '6px',
                  borderRadius: '7px', color: 'var(--gray-500)',
                }}
              >
                <Settings size={15} />
              </Link>
              <div style={S.userChip}>
                <div style={S.avatar}>{user.name[0].toUpperCase()}</div>
                <span>{user.name.split(' ')[0]}</span>
              </div>
              <LogoutBtn onClick={handleLogout} />
            </>
          ) : (
            <>
              <NavLink to="/login" active={isActive('/login')}>Sign in</NavLink>
              <CtaLink to="/register">+ Donate Blood</CtaLink>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
